import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { adminApi, type ParameterDiff, type ParameterSet } from '../api/admin';

export default function ParameterSetsPanel() {
  const queryClient = useQueryClient();

  const [year, setYear] = useState(new Date().getFullYear() + 1);
  const [editing, setEditing] = useState<ParameterSet | null>(null);
  const [draftJson, setDraftJson] = useState('');
  const [notes, setNotes] = useState('');
  const [diff, setDiff] = useState<ParameterDiff | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data: sets, isPending } = useQuery({
    queryKey: ['parameter-sets'],
    queryFn: adminApi.parameterSets,
  });

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong.');
  }

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['parameter-sets'] });

  const createDraft = useMutation({
    mutationFn: () => adminApi.createDraft(year),
    onSuccess: async (set) => {
      setError(null);
      open(set);
      await refresh();
    },
    onError: fail,
  });

  const save = useMutation({
    mutationFn: () => adminApi.saveDraft(editing!.id, draftJson, notes || null),
    onSuccess: async (set) => {
      setError(null);
      setEditing(set);
      await refresh();
    },
    onError: fail,
  });

  const showDiff = useMutation({
    mutationFn: () => adminApi.diff(editing!.id),
    onSuccess: (result) => {
      setError(null);
      setDiff(result);
    },
    onError: fail,
  });

  const activate = useMutation({
    mutationFn: () => adminApi.activate(editing!.id),
    onSuccess: async () => {
      setError(null);
      setEditing(null);
      setDiff(null);
      await refresh();
    },
    onError: fail,
  });

  function open(set: ParameterSet) {
    setEditing(set);
    // Pretty-printed so a human can actually edit rates and holiday dates by hand.
    try {
      setDraftJson(JSON.stringify(JSON.parse(set.dataJson), null, 2));
    } catch {
      setDraftJson(set.dataJson);
    }
    setNotes(set.notes ?? '');
    setDiff(null);
  }

  return (
    <>
      <section className="card">
        <h2>Parameter sets</h2>
        <p className="muted">
          A year's rates, thresholds and holiday calendar. Publishing a year is a data change here —
          no deploy. An active set cannot be edited; corrections create a new version.
        </p>

        <div className="toolbar">
          <input
            className="field__input field__input--short"
            type="number"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          />
          <button
            type="button"
            className="btn btn--primary"
            disabled={createDraft.isPending}
            onClick={() => createDraft.mutate()}
          >
            Create draft
          </button>
        </div>

        {error && <p className="status-error">{error}</p>}

        {isPending && <p className="muted">Loading…</p>}

        {sets && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Year</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Activated</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {sets.map((set) => (
                  <tr key={set.id}>
                    <td>{set.year}</td>
                    <td>v{set.version}</td>
                    <td>
                      <span className={`badge badge--${set.status.toLowerCase()}`}>{set.status}</span>
                    </td>
                    <td>
                      {set.activatedAt ? new Date(set.activatedAt).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td>
                      <button type="button" className="btn btn--small" onClick={() => open(set)}>
                        {set.status === 'Draft' ? 'Edit' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editing && (
        <section className="card">
          <div className="topbar">
            <h2>
              {editing.year} v{editing.version} · {editing.status}
            </h2>
            <button type="button" className="btn" onClick={() => setEditing(null)}>
              Close
            </button>
          </div>

          {editing.status !== 'Draft' && (
            <p className="muted">
              Read-only. This set is the audit trail for every declaration generated under it.
            </p>
          )}

          <textarea
            className="code"
            spellCheck={false}
            rows={22}
            readOnly={editing.status !== 'Draft'}
            value={draftJson}
            onChange={(e) => setDraftJson(e.target.value)}
          />

          {editing.status === 'Draft' && (
            <>
              <div className="field">
                <label className="field__label" htmlFor="notes">
                  Notes — record the source for each figure
                </label>
                <input
                  id="notes"
                  className="field__input"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              <div className="toolbar">
                <button
                  type="button"
                  className="btn"
                  disabled={save.isPending}
                  onClick={() => save.mutate()}
                >
                  Save draft
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={showDiff.isPending}
                  onClick={() => showDiff.mutate()}
                >
                  Review changes
                </button>
              </div>
            </>
          )}

          {diff && (
            <div className="card">
              <h3>
                Changes against{' '}
                {diff.comparedToVersion ? `v${diff.comparedToVersion}` : 'nothing — this is a first version'}
              </h3>

              {diff.changes.length === 0 ? (
                <p className="muted">No differences.</p>
              ) : (
                <div className="table-scroll">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Field</th>
                        <th>Before</th>
                        <th>After</th>
                      </tr>
                    </thead>
                    <tbody>
                      {diff.changes.map((change) => (
                        <tr key={change.path}>
                          <td>{change.path}</td>
                          <td className="muted">{change.before ?? '—'}</td>
                          <td className="strong">{change.after ?? '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Activation moves money and legal deadlines for every user, so it happens only
                  after the diff has been looked at. */}
              <p className="status-error">
                Activating supersedes the current set, recomputes every user's deadlines, and cannot
                be edited afterwards.
              </p>
              <button
                type="button"
                className="btn btn--primary"
                disabled={activate.isPending}
                onClick={() => activate.mutate()}
              >
                {activate.isPending ? 'Activating…' : `Activate ${editing.year} v${editing.version}`}
              </button>
            </div>
          )}
        </section>
      )}
    </>
  );
}
